import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import ContactForm from "../components/ContactForm";
import { breadcrumbSchema, serviceSchema } from "../lib/schemas";

const FEATURES = [
  {
    num: "01",
    title: "Gestione contratti",
    body:
      "Tutti i contratti sponsorship in un'unica vista. Asset per asset, scadenza per scadenza, KPI per KPI. Notifiche automatiche per rinnovi e milestone. Storico delle versioni contrattuali e tracciamento delle modifiche.",
  },
  {
    num: "02",
    title: "Attivazioni stagionali",
    body:
      "Calendario di matchday e attivazioni per ogni sponsor. Coordinamento tra il team commerciale, il team marketing e gli sponsor stessi. Eliminazione delle email manuali, workflow approvativi nativi.",
  },
  {
    num: "03",
    title: "Reporting tridimensionale",
    body:
      "Dashboard automatiche su Business Impact, Cultural Impact, Social Impact. Reportistica per sponsor pronta per essere consegnata, anziché costruita da zero ogni trimestre. Export in formati editabili dal cliente.",
  },
  {
    num: "04",
    title: "B2B networking tra sponsor",
    body:
      "Modulo che permette agli sponsor del club di conoscersi, fare cross-selling, costruire iniziative congiunte. È un valore commerciale nuovo che il club offre ai suoi sponsor — un argomento di rinnovo e di upsell.",
  },
  {
    num: "05",
    title: "Analytics & insight",
    body:
      "Benchmark del proprio portafoglio sponsorship rispetto al settore. Identificazione di asset dormienti e opportunità di upsell. Supporto alle trattative di rinnovo basato sui dati reali di attivazione.",
  },
];

const PROFILES = [
  {
    title: "Club professionistici",
    body:
      "Squadre delle leghe maggiori (calcio Serie A/B/C, basket, volley, rugby) che gestiscono portfolio sponsorship strutturati e vogliono trasformare la gestione commerciale.",
  },
  {
    title: "Leghe sportive",
    body:
      "Leghe nazionali che gestiscono diritti commerciali, sponsorship istituzionali e relazioni con multiple property affiliate.",
  },
  {
    title: "Federazioni",
    body:
      "Federazioni nazionali e internazionali con perimetro multi-disciplina, multi-evento, multi-paese che necessitano di infrastruttura scalabile.",
  },
];

const FAQS = [
  {
    q: "Quanto tempo serve per attivare PitchPartner?",
    a:
      "Il setup standard richiede 4-6 settimane (importazione contratti, configurazione utenti, training del team). Le implementazioni con integrazioni custom verso sistemi del cliente richiedono 8-12 settimane di onboarding coordinato.",
  },
  {
    q: "Avete un periodo di prova?",
    a:
      "Sì. La maggior parte dei clienti adotta PitchPartner dopo una fase di demo guidata su dati reali della propria organizzazione. Per i contesti più articolati prevediamo periodi di trial dedicati, concordati caso per caso.",
  },
  {
    q: "Possiamo migrare i nostri contratti attuali nel software?",
    a:
      "Sì. Importazione contratti inclusa nel setup. Il nostro team gestisce la migrazione, il cliente fornisce i contratti e il supporto del proprio team commerciale per la convalida finale.",
  },
  {
    q: "Il software è solo italiano?",
    a:
      "No. Multilingua attivo dal lancio: italiano, inglese, spagnolo, francese, tedesco. Per federazioni internazionali aggiungiamo lingue su richiesta in fase di implementazione.",
  },
  {
    q: "Posso vederlo prima di sottoscrivere?",
    a:
      "Sì. Demo guidata di 45 minuti su appuntamento. Possiamo lavorare anche su dati di esempio del tuo club per renderla il più concreta possibile.",
  },
];

const ServizioPitchPartner = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Servizi", url: "/servizi" },
    { name: "PitchPartner", url: "/servizi/pitchpartner" },
  ]);
  const serviceJsonLd = serviceSchema({
    name: "PitchPartner",
    description: "Software per costruire pitch di sponsorship sportiva data-driven.",
    slug: "pitchpartner",
  });

  return (
    <>
      <Seo
        title="PitchPartner: software pitch sponsorship data-driven | OnSports"
        description="PitchPartner è il software OnSports per costruire pitch di sponsorship sportiva data-driven. Per agenzie, property, club che cercano sponsor strutturati."
        path="/servizi/pitchpartner"
        jsonLd={[breadcrumbJsonLd, serviceJsonLd]}
      />

      {/* SEZ. 1 — HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-28"
        data-testid="hero-pitchpartner"
      >
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to="/servizi"
              className="inline-flex items-center gap-2 text-galactico/70 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-servizi"
            >
              <ArrowLeft size={16} /> Tutti i servizi
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <SectionEyebrow num="04" className="mt-8">
              PitchPartner · Tech &amp; Data
            </SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <span className="inline-block bg-lime text-dodgers font-condensed uppercase tracking-widest text-[13px] px-3 py-1.5 mt-6">
              Software SaaS · Sponsorship Management · B2B Networking
            </span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.15}>
            <h1
              className="display-h1 mt-8 max-w-full"
              style={{
                lineHeight: "0.95",
                letterSpacing: "-0.02em",
                fontSize: "clamp(36px, 7vw, 88px)",
              }}
            >
              <span className="block">La tecnologia</span>
              <span className="block">al servizio delle</span>
              <span className="block text-lime">SPONSORSHIP.</span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.25}>
            <p className="mt-10 body-lg opacity-85 max-w-[68ch]">
              PitchPartner è il software SaaS proprietario per club, leghe e
              federazioni che vogliono gestire le proprie sponsorship come
              piattaforme integrate: contratti, attivazioni, reporting
              tridimensionale, networking B2B tra sponsor. La configurazione
              del software si adatta alla scala e al perimetro di ogni
              cliente — niente tier rigidi, niente moduli forzati.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.32}>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] opacity-85">
              <span>Per — Club Serie A/B/C, leghe sportive, federazioni</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Modello — SaaS in configurazione custom</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Output — Piattaforma di gestione end-to-end</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 2 — IL PROBLEMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-problema">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Il problema</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Le sponsorship si gestiscono ancora con Excel ed email.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Una squadra di Serie A media gestisce 15-30 sponsor
                  attivi in parallelo. Ognuno ha un contratto diverso, un
                  set di asset diversi, esigenze di attivazione diverse,
                  scadenze diverse. Per la maggior parte dei club
                  italiani, questa complessità viene gestita con Excel,
                  calendar shared e thread di email. È un sistema che
                  funziona finché funziona — e che collassa al primo
                  errore di esecuzione.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Le conseguenze sono concrete. Asset contrattuali
                  dimenticati. Reporting impossibile da produrre senza
                  giornate di lavoro manuale. Difficoltà a fare
                  cross-selling tra sponsor del proprio portfolio.
                  Difficoltà a valorizzare la piattaforma commerciale del
                  club in fase di vendita di nuove partnership. Tempo del
                  team commerciale assorbito da operatività che non genera
                  ricavi.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Quello di cui le property sportive hanno bisogno non è
                  "un altro CRM". È un'infrastruttura specificamente
                  progettata per la natura delle sponsorship sportive:
                  contratti complessi con asset multipli, calendario
                  sportivo, attivazioni stagionali, reporting
                  multi-dimensionale. PitchPartner è quella
                  infrastruttura.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — COSA FA PITCHPARTNER */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-features">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Cosa fa PitchPartner</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Cinque capability, una sola piattaforma.
                </h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {FEATURES.map((f, i) => (
              <RevealOnScroll key={f.num} delay={i * 0.05}>
                <div
                  className="border border-lime p-6 md:p-8 bg-galactico/[0.02] h-full flex flex-col"
                  data-testid={`feature-${f.num}`}
                >
                  <span className="font-condensed uppercase tracking-widest text-lime text-[12px]">
                    ● {f.num}
                  </span>
                  <h3 className="mt-3 font-display text-xl md:text-2xl leading-tight uppercase">
                    {f.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {f.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 4 — CONFIGURAZIONE & ADOZIONE */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-configurazione">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Configurazione</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Una piattaforma. Configurata su misura.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  PitchPartner non viene venduto a pacchetti predefiniti.
                  Ogni implementazione viene configurata sulla scala e sul
                  perimetro del cliente: numero di contratti gestiti,
                  tipologia di asset, complessità del calendario sportivo,
                  esigenze di reporting istituzionale, integrazioni con i
                  sistemi esistenti. Per le federazioni e le leghe
                  nazionali, la piattaforma può essere personalizzata in
                  versione white-label sul brand del cliente.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Il setup iniziale prevede importazione dei contratti
                  esistenti, configurazione degli utenti, training del
                  team. Per implementazioni con integrazioni custom verso
                  CRM, ERP o sistemi finanziari del cliente, la fase di
                  onboarding è coordinata da un account manager dedicato.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — PER CHI È */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-per-chi-e">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Per chi è</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre tipologie di property.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {PROFILES.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.07}>
                <div
                  className="border border-lime p-6 md:p-8 h-full"
                  data-testid={`profile-${i}`}
                >
                  <h3 className="font-display text-lg md:text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90">
                    {p.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 6 — FAQ */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-faq">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Domande frequenti</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Le domande più frequenti.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="max-w-[960px]" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.04}>
                <details
                  className="group border-b border-dodgers/20 py-5 md:py-6"
                  data-testid={`faq-${i}`}
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-lg md:text-xl leading-tight pr-4">
                      {f.q}
                    </span>
                    <span
                      className="flex-shrink-0 mt-1 text-lime transition-transform duration-300 group-open:rotate-45"
                      aria-hidden="true"
                    >
                      <Plus size={22} strokeWidth={2.2} />
                    </span>
                  </summary>
                  <p className="mt-4 text-[15px] md:text-[1rem] leading-[1.7] opacity-90 max-w-[760px]">
                    {f.a}
                  </p>
                </details>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 7 — CONTATTO IN-PAGE */}
      <section
        className="bg-galactico text-dodgers py-24 md:py-32 border-t border-dodgers/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-3xl">
          <RevealOnScroll>
            <SectionEyebrow>● Cominciamo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <h2 className="display-h2 mt-6 max-w-[28ch]" data-testid="cta-h2">
              Richiedi una <span className="text-lime">DEMO</span> di PitchPartner.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Compila il form qui sotto, ti rispondiamo entro 24 ore lavorative.
              45 minuti di demo guidata su un caso concreto del tuo club.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10">
              <ContactForm
                tone="dark"
                defaultSubject="Richiesta demo — PitchPartner"
                compact
              />
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </>
  );
};

export default ServizioPitchPartner;
